import { useCallback, useEffect, useRef, useState } from "react";
import {
  oauthHandleRedirectIfPresent,
  oauthLoginUrl,
  type OAuthResult,
} from "@huggingface/hub";

/**
 * HF OAuth flow, reused from `reachy_mini_minimal_conversation/src/main.ts`.
 *
 * - On a Space: the client ID is injected into `window.huggingface.variables`
 *   by `docker-entrypoint.d/10-inject-hf-vars.sh` at container start.
 * - Locally: we read it from `VITE_HF_OAUTH_CLIENT_ID` (`.env.local`).
 */

const STORAGE_KEY = "hf-oauth-result-v1";

function readClientId(): string | null {
  const injected = window.huggingface?.variables?.OAUTH_CLIENT_ID;
  if (injected) return injected;
  return import.meta.env.VITE_HF_OAUTH_CLIENT_ID ?? null;
}

function readScopes(): string {
  return (
    window.huggingface?.variables?.OAUTH_SCOPES ??
    "openid profile write-repos manage-repos"
  );
}

function loadStoredResult(): OAuthResult | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OAuthResult;
    const expiresAt = parsed.accessTokenExpiresAt
      ? new Date(parsed.accessTokenExpiresAt).getTime()
      : 0;
    if (expiresAt && expiresAt < Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    parsed.accessTokenExpiresAt = new Date(parsed.accessTokenExpiresAt);
    return parsed;
  } catch {
    return null;
  }
}

function storeResult(result: OAuthResult): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
}

export interface HfAuthState {
  isReady: boolean;
  isSignedIn: boolean;
  isSigningIn: boolean;
  user: OAuthResult["userInfo"] | null;
  accessToken: string | null;
  error: string | null;
  clientId: string | null;
  signIn: () => Promise<void>;
  signOut: () => void;
}

export function useHfAuth(): HfAuthState {
  const [result, setResult] = useState<OAuthResult | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientIdRef = useRef<string | null>(readClientId());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const redirectResult = await oauthHandleRedirectIfPresent();
        if (cancelled) return;
        if (redirectResult) {
          storeResult(redirectResult);
          setResult(redirectResult);
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.toString());
          setIsReady(true);
          return;
        }
        const stored = loadStoredResult();
        if (stored) setResult(stored);
        setIsReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "OAuth error");
        setIsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    setError(null);
    const clientId = clientIdRef.current;
    if (!clientId) {
      setError(
        "No Hugging Face OAuth client ID configured. On a Space this should be auto-injected; locally set VITE_HF_OAUTH_CLIENT_ID in frontend/.env.local.",
      );
      return;
    }
    setIsSigningIn(true);
    try {
      const url = await oauthLoginUrl({
        clientId,
        scopes: readScopes(),
        redirectUrl: window.location.origin + window.location.pathname,
      });
      window.location.href = url;
    } catch (e) {
      setIsSigningIn(false);
      setError(e instanceof Error ? e.message : "Failed to start OAuth flow");
    }
  }, []);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setResult(null);
  }, []);

  return {
    isReady,
    isSignedIn: !!result?.accessToken,
    isSigningIn,
    user: result?.userInfo ?? null,
    accessToken: result?.accessToken ?? null,
    error,
    clientId: clientIdRef.current,
    signIn,
    signOut,
  };
}
